import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Filter, Zap, Eye, Pencil, X, CheckCircle, ChevronDown, ChevronRight, History, Wrench, Clock, Bell, Search } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getApiUrl } from "../config/api";
import authFetch from '../utils/authFetch';
import LoadingSpinner from "../components/ui/LoadingSpinner";

const statusVariants = {
  pending: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  in_progress: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
  completed: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  cancelled: "bg-gray-50 text-gray-600 ring-1 ring-gray-200",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Render an AI-supplied estimated_cost as a localized USD string. Returns null
// for missing/non-numeric input so callers can short-circuit rendering.
const formatEstimatedCost = (cost) => {
  if (cost === null || cost === undefined || cost === "") return null;
  const n = typeof cost === "number" ? cost : Number(cost);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

// Renders a formatted estimated-cost badge, or nothing when the cost is
// missing/non-numeric. Centralizes the format + null-check that used to be
// inlined as IIFEs at multiple render sites.
//   - variant: "neutral" (gray pill) or "success" (emerald pill)
//   - prefix:  optional text rendered before the cost (e.g. "Estimated Cost")
//   - label:   optional outer label; when set, the badge is wrapped in a
//              row with a "<label>: <badge>" layout
//   - title:   optional native tooltip on the badge
const CostBadge = ({ cost, variant = "neutral", prefix, label, title }) => {
  const formatted = formatEstimatedCost(cost);
  if (!formatted) return null;

  const styles =
    variant === "success"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200 px-2 py-0.5 font-semibold"
      : "bg-gray-100 text-gray-700 ring-gray-200 px-2.5 py-1 font-medium";

  const badge = (
    <span
      className={`inline-flex items-center rounded-full text-xs ring-1 ${styles}`}
      title={title}
    >
      {prefix ? `${prefix} ` : ""}{formatted}
    </span>
  );

  if (label) {
    return (
      <div className="mb-2 flex items-center gap-2">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        {badge}
      </div>
    );
  }
  return badge;
};

export default function ServiceRequestsPage() {
  const location = useLocation();
  const prefillAppliedRef = useRef(false);

  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState("all");
  // 'open' shows only open table, 'past' only past, 'all' shows both
  const [viewSegment, setViewSegment] = useState("all");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Map of service_request_id -> latest diagnosis (or null if none).
  // Pre-loaded so the AI DIAGNOSIS column on the open-requests table
  // has data on first paint without per-row fetches.
  const [latestDiagnoses, setLatestDiagnoses] = useState({});
  
  // Data from API
  const [serviceRequests, setServiceRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [appliances, setAppliances] = useState([]);
  const [tenants, setTenants] = useState([]);

  const [newRequest, setNewRequest] = useState({
    tenantId: "",
    propertyId: "",
    applianceId: "",
    serviceScheduleDate: "",
    tenantDescription: "",
    landlordDescription: "",
    requestStatus: "pending",
  });

  // View/Edit modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  // AI Diagnosis modal state
  const [showDiagnosisModal, setShowDiagnosisModal] = useState(false);
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [diagnosisRequestName, setDiagnosisRequestName] = useState("");

  // Latest Diagnosis modal state
  const [showLatestDiagnosisModal, setShowLatestDiagnosisModal] = useState(false);
  const [latestDiagnosisResult, setLatestDiagnosisResult] = useState(null);
  const [latestDiagnosisLoading, setLatestDiagnosisLoading] = useState(false);
  const [latestDiagnosisRequestName, setLatestDiagnosisRequestName] = useState("");

  // Diagnosis history state — now lazy-loaded inside the View Details modal
  // (formerly an inline expandable sub-row on each table row).
  const [diagnosisHistory, setDiagnosisHistory] = useState({});
  const [diagnosisHistoryLoading, setDiagnosisHistoryLoading] = useState({});
  const [showHistoryInModal, setShowHistoryInModal] = useState(false);

  // View single diagnosis from history modal
  const [showHistoryDiagnosisModal, setShowHistoryDiagnosisModal] = useState(false);
  const [historyDiagnosisResult, setHistoryDiagnosisResult] = useState(null);
  const [historyDiagnosisRequestName, setHistoryDiagnosisRequestName] = useState("");

  // Pre-diagnosis confirmation modal state
  const [showPreDiagnosisModal, setShowPreDiagnosisModal] = useState(false);
  const [preDiagnosisRequest, setPreDiagnosisRequest] = useState(null);
  const [preDiagnosisLandlordDescription, setPreDiagnosisLandlordDescription] = useState("");
  const [preDiagnosisSubmitting, setPreDiagnosisSubmitting] = useState(false);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Handle prefill from Messages page route state
  useEffect(() => {
    if (prefillAppliedRef.current || loading) return;
    const state = location.state;
    if (!state?.prefill) return;
    prefillAppliedRef.current = true;

    // Use propertyId directly if the backend provided it, otherwise match by address
    let resolvedPropertyId = "";
    if (state.propertyId) {
      const exists = properties.find((p) => p.id === state.propertyId);
      if (exists) resolvedPropertyId = exists.id.toString();
    }
    if (!resolvedPropertyId && state.propertyAddress) {
      const matched = properties.find((p) => p.address === state.propertyAddress);
      if (matched) resolvedPropertyId = matched.id.toString();
    }

    setNewRequest({
      tenantId: state.tenantId != null ? String(state.tenantId) : "",
      propertyId: resolvedPropertyId,
      applianceId: "",
      serviceScheduleDate: "",
      tenantDescription: state.tenantDescription || "",
      landlordDescription: "",
      requestStatus: "pending",
    });
    setShowNewModal(true);

    window.history.replaceState({}, document.title);
  }, [loading, location.state, properties]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [requests] = await Promise.all([
        fetchServiceRequests(),
        fetchProperties(),
        fetchAppliances(),
        fetchTenants(),
      ]);
      // Best-effort preload of latest diagnoses; silently ignore failures
      if (requests && requests.length) {
        fetchAllLatestDiagnoses(requests);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceRequests = async () => {
    try {
      const response = await authFetch(getApiUrl('/api/v1/service/list'));

      if (response.ok) {
        const data = await response.json();
        const requests = data.service_requests || [];
        setServiceRequests(requests);
        return requests;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch service requests');
      }
    } catch (err) {
      console.error('Error fetching service requests:', err);
      setError('An error occurred while fetching service requests');
    }
    return [];
  };

  // Fire one /diagnosis/latest request per service request in parallel.
  // Failures (e.g. 404 when no diagnosis exists) are swallowed so rows
  // simply render the muted "Not diagnosed" state.
  const fetchAllLatestDiagnoses = async (requests) => {
    const results = await Promise.all(
      requests.map(async (req) => {
        try {
          const response = await authFetch(
            getApiUrl(`/api/v1/service/diagnosis/latest?service_request_id=${req.id}`)
          );
          if (response.ok) {
            const data = await response.json();
            return [req.id, data.service_diagnosis || null];
          }
        } catch (err) {
          // ignore per-row failures
        }
        return [req.id, null];
      })
    );
    const map = {};
    for (const [id, diag] of results) {
      map[id] = diag;
    }
    setLatestDiagnoses(map);
  };

  const fetchProperties = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        setError('User not authenticated');
        return;
      }

      const response = await authFetch(getApiUrl('/api/v1/property/list'));

      if (response.ok) {
        const data = await response.json();
        setProperties(data.properties || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch properties');
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setError('An error occurred while fetching properties');
    }
  };

  const fetchAppliances = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        return;
      }

      const response = await authFetch(getApiUrl(`/api/v1/appliance/list?user_id=${userId}`));

      if (response.ok) {
        const data = await response.json();
        // Transform appliances data
        const appliancesData = [];
        data.properties.forEach(prop => {
          prop.appliances.forEach(app => {
            appliancesData.push({
              id: app.id,
              propertyId: prop.property_id,
              name: `${app.category}${app.description ? ' - ' + app.description : ''}`,
              category: app.category,
              description: app.description,
              make: app.make,
              model: app.model,
            });
          });
        });
        setAppliances(appliancesData);
      }
    } catch (err) {
      console.error('Error fetching appliances:', err);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await authFetch(getApiUrl('/api/v1/tenant/list'));

      if (response.ok) {
        const data = await response.json();
        setTenants(
          (data.tenants || []).map((t) => ({
            id: t.id,
            name: `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email,
            propertyId: t.property_id,
          }))
        );
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  const openRequests = serviceRequests.filter(
    (r) => r.request_status === "pending" || r.request_status === "in_progress"
  );
  const pastRequests = serviceRequests.filter(
    (r) => r.request_status === "completed" || r.request_status === "cancelled"
  );

  const filteredOpenRequests =
    selectedPropertyId === "all"
      ? openRequests
      : openRequests.filter((r) => r.property_id === Number(selectedPropertyId));

  const filteredPastRequests =
    selectedPropertyId === "all"
      ? pastRequests
      : pastRequests.filter((r) => r.property_id === Number(selectedPropertyId));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRequest((prev) => ({ ...prev, [name]: value }));
  };

  const handlePropertyChange = (value) => {
    const propertyTenant = tenants.find((t) => t.propertyId === parseInt(value));
    setNewRequest((prev) => ({
      ...prev,
      propertyId: value,
      tenantId: propertyTenant ? propertyTenant.id.toString() : prev.tenantId,
    }));
  };

  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 15;

  const validateFiles = (files) => {
    if (files.length > MAX_FILES) {
      return `You can upload at most ${MAX_FILES} images.`;
    }
    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        return `"${file.name}" is not a supported file type. Only .jpg and .png are allowed.`;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        return `"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB size limit.`;
      }
    }
    return null;
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      setUploadedFiles([]);
      setFileError(null);
      return;
    }
    const fileArray = Array.from(files);
    const validationErr = validateFiles(fileArray);
    if (validationErr) {
      setFileError(validationErr);
      e.target.value = '';
      setUploadedFiles([]);
      return;
    }
    setFileError(null);
    setUploadedFiles(fileArray);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!newRequest.propertyId) {
      setError('Please select a property');
      return;
    }
    if (!newRequest.tenantDescription || newRequest.tenantDescription.trim() === '') {
      setError('Please enter a description');
      return;
    }
    if (uploadedFiles.length > 0) {
      const fileValidationErr = validateFiles(uploadedFiles);
      if (fileValidationErr) {
        setFileError(fileValidationErr);
        return;
      }
    }
    
    try {
      const formData = new FormData();
      formData.append('property_id', parseInt(newRequest.propertyId, 10));
      const tenantId = newRequest.tenantId && newRequest.tenantId !== "" && newRequest.tenantId !== "none"
        ? parseInt(newRequest.tenantId, 10) : '';
      if (tenantId) formData.append('tenant_id', tenantId);
      if (newRequest.applianceId && newRequest.applianceId !== "") {
        formData.append('appliance_id', parseInt(newRequest.applianceId, 10));
      }
      formData.append('tenant_description', newRequest.tenantDescription.trim());
      if (newRequest.landlordDescription?.trim()) {
        formData.append('landlord_description', newRequest.landlordDescription.trim());
      }
      if (newRequest.serviceScheduleDate) {
        formData.append('service_schedule_date', newRequest.serviceScheduleDate);
      }
      formData.append('request_status', newRequest.requestStatus);

      uploadedFiles.forEach((file) => {
        formData.append('images', file);
      });

      const response = await authFetch(getApiUrl('/api/v1/service/create'), {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        // Refresh the service requests list
        await fetchServiceRequests();
        
        // Reset form
        setNewRequest({
          tenantId: "",
          propertyId: "",
          applianceId: "",
          serviceScheduleDate: "",
          tenantDescription: "",
          landlordDescription: "",
          requestStatus: "pending",
        });
        setUploadedFiles([]);
        setFileError(null);
        setShowNewModal(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create service request');
      }
    } catch (err) {
      console.error('Error creating service request:', err);
      setError('An error occurred while creating service request');
    }
  };

  const handleDiagnoseWithAI = async (req) => {
    // Open pre-diagnosis modal to show context and allow landlord description input
    // Fetch full service request details first
    const details = await fetchServiceRequestDetails(req.id);
    if (details) {
      setPreDiagnosisRequest(details);
      setPreDiagnosisLandlordDescription(details.landlord_description || "");
      setShowPreDiagnosisModal(true);
    }
  };

  const handleConfirmDiagnosis = async () => {
    if (!preDiagnosisRequest) return;

    try {
      setPreDiagnosisSubmitting(true);

      // If landlord description has changed, update the service request first
      if (preDiagnosisLandlordDescription !== (preDiagnosisRequest.landlord_description || "")) {
        const updateResponse = await authFetch(getApiUrl('/api/v1/service/update'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            service_request_id: preDiagnosisRequest.id,
            landlord_description: preDiagnosisLandlordDescription.trim() || null,
          }),
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          setError(errorData.error || 'Failed to update landlord description');
          setPreDiagnosisSubmitting(false);
          return;
        }

        // Refresh service requests list to show updated data
        await fetchServiceRequests();
      }

      // Close pre-diagnosis modal and open diagnosis modal
      setShowPreDiagnosisModal(false);

      // Set up the diagnosis modal with loading state
      const applianceName = getApplianceName(preDiagnosisRequest.appliance_id) ?? "General Issue";
      setDiagnosisRequestName(`#${preDiagnosisRequest.id} - ${applianceName}`);
      setDiagnosisResult(null);
      setDiagnosisLoading(true);
      setShowDiagnosisModal(true);

      const response = await authFetch(getApiUrl('/api/v1/service/diagnose'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ service_request_id: preDiagnosisRequest.id }),
      });

      if (response.ok) {
        const data = await response.json();
        setDiagnosisResult(data.diagnosis);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to get AI diagnosis');
        setShowDiagnosisModal(false);
      }
    } catch (err) {
      console.error('Error getting AI diagnosis:', err);
      setError('An error occurred while getting AI diagnosis');
      setShowDiagnosisModal(false);
    } finally {
      setDiagnosisLoading(false);
      setPreDiagnosisSubmitting(false);
      setPreDiagnosisRequest(null);
      setPreDiagnosisLandlordDescription("");
    }
  };

  const closePreDiagnosisModal = () => {
    setShowPreDiagnosisModal(false);
    setPreDiagnosisRequest(null);
    setPreDiagnosisLandlordDescription("");
  };

  const handleViewLatestDiagnosis = async (req) => {
    try {
      // Set up the modal with loading state
      const applianceName = getApplianceName(req.appliance_id) ?? "General Issue";
      setLatestDiagnosisRequestName(`#${req.id} - ${applianceName}`);
      setLatestDiagnosisResult(null);
      setLatestDiagnosisLoading(true);
      setShowLatestDiagnosisModal(true);

      const response = await authFetch(getApiUrl(`/api/v1/service/diagnosis/latest?service_request_id=${req.id}`));

      if (response.ok) {
        const data = await response.json();
        setLatestDiagnosisResult(data.service_diagnosis);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to get latest diagnosis');
        setShowLatestDiagnosisModal(false);
      }
    } catch (err) {
      console.error('Error getting latest diagnosis:', err);
      setError('An error occurred while getting latest diagnosis');
      setShowLatestDiagnosisModal(false);
    } finally {
      setLatestDiagnosisLoading(false);
    }
  };

  const fetchDiagnosisHistory = async (requestId) => {
    try {
      setDiagnosisHistoryLoading(prev => ({ ...prev, [requestId]: true }));

      const response = await authFetch(getApiUrl(`/api/v1/service/diagnosis?service_request_id=${requestId}`));

      if (response.ok) {
        const data = await response.json();
        const diagnoses = data.service_diagnoses || [];
        setDiagnosisHistory(prev => ({ ...prev, [requestId]: diagnoses }));
        return diagnoses;
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch diagnosis history:', errorData.error);
        setDiagnosisHistory(prev => ({ ...prev, [requestId]: [] }));
        return [];
      }
    } catch (err) {
      console.error('Error fetching diagnosis history:', err);
      setDiagnosisHistory(prev => ({ ...prev, [requestId]: [] }));
      return [];
    } finally {
      setDiagnosisHistoryLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleViewHistoryDiagnosis = (diagnosis, requestId, applianceId) => {
    const applianceName = getApplianceName(applianceId) ?? "General Issue";
    setHistoryDiagnosisRequestName(`#${requestId} - ${applianceName}`);
    setHistoryDiagnosisResult(diagnosis);
    setShowHistoryDiagnosisModal(true);
  };

  const formatDiagnosisDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Reusable diagnosis content component
  // Uses the fresh diagnosis format: device object with appliance_type/brand/model,
  // and parts_to_order per cause in top_causes
  const DiagnosisContent = ({ result, showAiModel = false }) => {
    if (!result) return null;

    const { device, ai_model, top_causes, safety_warnings, disposition, recommendation } = result;

    const formatDeviceString = () => {
      if (!device) return "Unknown";
      let str = device.appliance_type?.replace(/_/g, " ") || "Unknown";
      if (device.brand) str += ` - ${device.brand}`;
      if (device.model) str += ` (${device.model})`;
      return str;
    };

    return (
      <div className="space-y-5 pt-2">
        {/* Device Info */}
        {device && (device.appliance_type || device.brand || device.model) && (
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Device</h4>
            <p className="text-sm font-medium text-gray-900">{formatDeviceString()}</p>
          </div>
        )}

        {/* AI Model Info - optional */}
        {showAiModel && ai_model && (
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">AI Model</h4>
            <p className="text-sm font-medium text-gray-900">{ai_model}</p>
          </div>
        )}

        {/* Recommendation */}
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <h4 className="text-xs uppercase tracking-wide text-green-700 mb-2">Recommendation</h4>
          <p className="text-sm text-gray-900">{recommendation}</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
              disposition === "DIY"
                ? "bg-emerald-100 text-emerald-700"
                : disposition === "DIY_with_caution"
                ? "bg-amber-100 text-amber-700"
                : "bg-red-100 text-red-700"
            }`}>
              {disposition?.replace(/_/g, " ")}
            </span>
            {/* Estimated cost for the top (most likely) cause - quick at-a-glance
                $$ signal next to the dispatch decision. */}
            <CostBadge
              cost={top_causes?.[0]?.estimated_cost}
              prefix="Estimated Cost"
              title="Estimated cost for the most likely cause"
            />
          </div>
        </div>

        {/* Top Causes */}
        <div>
          <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-3">Possible Causes</h4>
          <div className="space-y-3">
            {top_causes?.map((cause, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-gray-900">{cause.name}</h5>
                  <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                    {Math.round((cause.probability || 0) * 100)}% likely
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{cause.why_this}</p>

                {cause.checks?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Checks:</p>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                      {cause.checks.map((check, cIdx) => (
                        <li key={cIdx}>{check}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {cause.fix?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-gray-500 mb-1">Fix:</p>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                      {cause.fix.map((step, fIdx) => (
                        <li key={fIdx}>{step}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <CostBadge
                  cost={cause.estimated_cost}
                  variant="success"
                  label="Estimated fix cost:"
                />

                {cause.parts_to_order?.length > 0 && (
                  <div className="mt-2 bg-blue-50 rounded-lg p-2 border border-blue-200">
                    <p className="text-xs font-medium text-blue-700 mb-1">Parts to Order:</p>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                      {cause.parts_to_order.map((part, pIdx) => (
                        <li key={pIdx}>{part}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Safety Warnings */}
        {safety_warnings?.length > 0 && (
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <h4 className="text-xs uppercase tracking-wide text-red-700 mb-2">⚠️ Safety Warnings</h4>
            <ul className="list-disc list-inside text-sm text-gray-900 space-y-1">
              {safety_warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const handleAcknowledge = async (req) => {
    try {
      const payload = {
        service_request_id: req.id,
        acknowledged_date: new Date().toISOString(),
        request_status: 'in_progress',
      };

      const response = await authFetch(getApiUrl('/api/v1/service/update'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Refresh the service requests list
        await fetchServiceRequests();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to acknowledge service request');
      }
    } catch (err) {
      console.error('Error acknowledging service request:', err);
      setError('An error occurred while acknowledging service request');
    }
  };

  const fetchServiceRequestDetails = async (requestId) => {
    try {
      const response = await authFetch(getApiUrl(`/api/v1/service/get?service_request_id=${requestId}`));

      if (response.ok) {
        const data = await response.json();
        return data.service_request;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch service request details');
        return null;
      }
    } catch (err) {
      console.error('Error fetching service request details:', err);
      setError('An error occurred while fetching service request details');
      return null;
    }
  };

  const handleViewClick = async (req) => {
    const details = await fetchServiceRequestDetails(req.id);
    if (details) {
      setSelectedRequest(details);
      setEditFormData({
        tenantId: details.tenant_id || "",
        propertyId: details.property_id ? details.property_id.toString() : "",
        applianceId: details.appliance_id ? details.appliance_id.toString() : "",
        serviceScheduleDate: details.service_schedule_date ? details.service_schedule_date.slice(0, 16) : "",
        tenantDescription: details.tenant_description || "",
        requestStatus: details.request_status || "pending",
      });
      setIsEditing(false);
      setShowHistoryInModal(false);
      setShowViewModal(true);
    }
  };

  const handleToggleHistoryInModal = async () => {
    if (!selectedRequest) return;
    const willOpen = !showHistoryInModal;
    if (willOpen && !diagnosisHistory[selectedRequest.id]) {
      await fetchDiagnosisHistory(selectedRequest.id);
    }
    setShowHistoryInModal(willOpen);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        service_request_id: selectedRequest.id,
        property_id: parseInt(editFormData.propertyId, 10),
        tenant_id: editFormData.tenantId && editFormData.tenantId !== "" ? parseInt(editFormData.tenantId, 10) : null,
        appliance_id: editFormData.applianceId && editFormData.applianceId !== "" ? parseInt(editFormData.applianceId, 10) : null,
        tenant_description: editFormData.tenantDescription.trim(),
        service_schedule_date: editFormData.serviceScheduleDate || null,
        request_status: editFormData.requestStatus,
      };

      const response = await authFetch(getApiUrl('/api/v1/service/update'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        // Refresh the service requests list
        await fetchServiceRequests();
        setIsEditing(false);
        setShowViewModal(false);
        setSelectedRequest(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update service request');
      }
    } catch (err) {
      console.error('Error updating service request:', err);
      setError('An error occurred while updating service request');
    }
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedRequest(null);
    setIsEditing(false);
    setShowHistoryInModal(false);
  };

  const openCreate = () => {
    setNewRequest({
      tenantId: "",
      propertyId: "",
      applianceId: "",
      serviceScheduleDate: "",
      tenantDescription: "",
      requestStatus: "pending",
    });
    setUploadedFiles([]);
    setFileError(null);
    setShowNewModal(true);
  };

  // Helper function to get property name by ID
  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : "Unknown Property";
  };

  // Helper function to get appliance name by ID
  const getApplianceName = (applianceId) => {
    const appliance = appliances.find(a => a.id === applianceId);
    return appliance ? appliance.name : null;
  };

  // Lookup tenant record by id (returns the local shape produced by fetchTenants)
  const getTenant = (tenantId) => tenants.find((t) => t.id === tenantId);

  // Two-letter initials for the tenant avatar (e.g. "Jamila Reyes" -> "JR").
  // Falls back to the first two letters of whatever single token is available.
  const getTenantInitials = (tenantId) => {
    const t = getTenant(tenantId);
    if (!t || !t.name) return "?";
    const parts = t.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  };

  // Build the title shown in the REQUEST column. Prefers the appliance
  // category (e.g. "Dishwasher"), falling back to "General Issue".
  const getRequestTitle = (req) => {
    const appliance = appliances.find((a) => a.id === req.appliance_id);
    if (appliance) return appliance.category || appliance.name || "Service Request";
    return "General Issue";
  };

  return (
    <Layout pageTitle="Service Requests">
      <div className="w-full bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Service Requests</h1>
            <p className="text-sm text-gray-600">
              Track maintenance issues across your properties, capture photos, and triage with AI.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* PLACEHOLDER: search is unwired — no onChange handler yet. */}
            <div className="relative w-72 hidden md:block">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="search"
                placeholder="Search by tenant, property, appliance..."
                className="pl-9 rounded-full bg-white border-gray-200 h-10 text-sm"
                aria-label="Search service requests"
              />
            </div>
            <Button
              className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm"
              onClick={openCreate}
              disabled={loading || properties.length === 0}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              New Request
            </Button>
            {/* PLACEHOLDER: notifications bell — no notifications API wired yet. */}
            <button
              type="button"
              aria-label="Notifications"
              className="relative h-10 w-10 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              <Bell className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>

        {/* Stat tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {/* Open count is live; sub-stat is a PLACEHOLDER (no priority field). */}
          <Card className="shadow-sm bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500">Open</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{openRequests.length}</p>
              {/* PLACEHOLDER: priority breakdown — backend has no priority field. */}
              <p className="text-xs text-gray-400 mt-1">— high priority</p>
            </CardContent>
          </Card>
          {/* PLACEHOLDER: "Awaiting AI" count — not yet computed. */}
          <Card className="shadow-sm bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500">Awaiting AI</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">—</p>
              <p className="text-xs text-red-500 mt-1">Re-run after photos</p>
            </CardContent>
          </Card>
          {/* PLACEHOLDER: avg resolution time — backend has no completion timestamps wired. */}
          <Card className="shadow-sm bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500">Avg. resolution</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">—</p>
              <p className="text-xs text-gray-400 mt-1">— this month</p>
            </CardContent>
          </Card>
          {/* PLACEHOLDER: 30-day spend — no actual cost field stored. */}
          <Card className="shadow-sm bg-white">
            <CardContent className="p-4">
              <p className="text-xs text-gray-500">Spent &middot; 30d</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">$—</p>
              <p className="text-xs text-gray-400 mt-1">across — vendors</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Card */}
        <Card className="mt-6 shadow-sm bg-white">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                <div className="flex items-center gap-2 mr-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Filter</span>
                </div>
                {/* "All properties" chip */}
                <button
                  type="button"
                  onClick={() => setSelectedPropertyId("all")}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    selectedPropertyId === "all"
                      ? "bg-green-50 text-green-700 border-green-200 ring-1 ring-green-200"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  All properties
                </button>
                {/* One chip per property with live Open/Past sub-counts */}
                {properties.map((p) => {
                  const openCount = openRequests.filter((r) => r.property_id === p.id).length;
                  const pastCount = pastRequests.filter((r) => r.property_id === p.id).length;
                  const active = selectedPropertyId === p.id.toString();
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPropertyId(p.id.toString())}
                      className={`flex flex-col items-start px-3 py-1.5 rounded-2xl text-xs border transition-colors text-left ${
                        active
                          ? "bg-green-50 text-green-700 border-green-200 ring-1 ring-green-200"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <span className="font-medium leading-tight">{p.name}</span>
                      <span className={`text-[10px] leading-tight ${active ? "text-green-600" : "text-gray-400"}`}>
                        Open {openCount} &middot; Past {pastCount}
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Open / Past / All segment control */}
              <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-0.5">
                {[
                  { value: "open", label: "Open" },
                  { value: "past", label: "Past" },
                  { value: "all", label: "All" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setViewSegment(opt.value)}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      viewSegment === opt.value
                        ? "bg-green-50 text-green-700 ring-1 ring-green-200"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Open requests */}
        {viewSegment !== "past" && (
          <div className="mt-6">
            <Card className="border shadow-sm bg-white">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">Open requests</CardTitle>
                <div className="text-xs text-gray-500">
                  Sorted by priority, then age. Click a row to open the full request.
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {filteredOpenRequests.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
                    No open service requests for this property.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-separate border-spacing-y-2">
                      <thead>
                        <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                          <th className="px-3 py-2">Request</th>
                          <th className="px-3 py-2">
                            Property <span className="text-gray-300">·</span> Tenant
                          </th>
                          <th className="px-3 py-2">AI Diagnosis</th>
                          <th className="px-3 py-2">Vendor</th>
                          <th className="px-3 py-2">Cost</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOpenRequests.map((req) => {
                          const tenant = getTenant(req.tenant_id);
                          const latest = latestDiagnoses[req.id];
                          const topCause = latest?.top_causes?.[0];
                          const topCauseName = topCause?.name || latest?.recommendation || null;
                          // The diagnosis API returns a per-cause probability in [0,1].
                          // Use the top (most likely) cause's probability as the row-level
                          // confidence indicator: percentage label + 5-dot bar (each dot ≈ 20%).
                          const probability = typeof topCause?.probability === "number" ? topCause.probability : null;
                          const confidencePct = probability !== null ? Math.round(probability * 100) : null;
                          const filledDots = confidencePct !== null
                            ? Math.max(1, Math.min(5, Math.round(confidencePct / 20)))
                            : 0;
                          return (
                            <tr
                              key={req.id}
                              onClick={() => handleViewClick(req)}
                              className="bg-white rounded-xl shadow-sm align-top cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                              {/* REQUEST */}
                              <td className="px-3 py-3 rounded-l-xl">
                                <div className="flex items-start gap-3">
                                  <div className="h-9 w-9 rounded-lg bg-green-600/10 flex items-center justify-center flex-shrink-0">
                                    <Wrench className="h-4 w-4 text-green-600" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {getRequestTitle(req)}{" "}
                                      <span className="text-gray-400 font-normal">&middot; #{req.id}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 truncate max-w-xs">
                                      {req.tenant_description || "No description"}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              {/* PROPERTY · TENANT */}
                              <td className="px-3 py-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {getPropertyName(req.property_id)}
                                </div>
                                <div className="mt-1 flex items-center gap-2">
                                  {req.tenant_id ? (
                                    <>
                                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-700">
                                        {getTenantInitials(req.tenant_id)}
                                      </span>
                                      <span className="text-xs text-gray-600 truncate">
                                        {tenant?.name || `Tenant #${req.tenant_id}`}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </div>
                              </td>
                              {/* AI DIAGNOSIS — top-cause probability from /diagnosis/latest */}
                              <td className="px-3 py-3">
                                {topCauseName ? (
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-xs">
                                      <span className="tracking-tighter" aria-hidden="true">
                                        <span className="text-gray-700">{"\u25CF".repeat(filledDots)}</span>
                                        <span className="text-gray-300">{"\u25CF".repeat(5 - filledDots)}</span>
                                      </span>
                                      <span className="text-gray-600 font-medium">
                                        {confidencePct !== null ? `${confidencePct}%` : "\u2014%"}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-700 truncate max-w-[14rem]">
                                      {topCauseName}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Not diagnosed</span>
                                )}
                              </td>
                              {/* VENDOR — PLACEHOLDER: backend has no vendor field yet */}
                              <td className="px-3 py-3">
                                <span className="inline-flex items-center rounded-full bg-gray-50 text-gray-500 ring-1 ring-gray-200 px-2.5 py-1 text-xs">
                                  <Wrench className="mr-1 h-3 w-3" />
                                  —
                                </span>
                              </td>
                              {/* COST — derived from the top cause of the latest diagnosis;
                                  only rendered when the diagnosis has BOTH a probability and a
                                  numeric estimated_cost. Falls back to the muted "$—" placeholder
                                  otherwise (no diagnosis yet, or diagnosis lacks a cost). */}
                              <td className="px-3 py-3 text-sm text-gray-500">
                                {probability !== null && formatEstimatedCost(topCause?.estimated_cost) ? (
                                  <CostBadge
                                    cost={topCause.estimated_cost}
                                    title="Estimated cost from latest AI diagnosis"
                                  />
                                ) : (
                                  "$—"
                                )}
                              </td>
                              {/* STATUS */}
                              <td className="px-3 py-3">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusVariants[req.request_status]}`}
                                >
                                  {req.request_status.replace("_", " ")}
                                </span>
                              </td>
                              {/* ACTIONS — view latest diagnosis (read-only) + diagnose with AI */}
                              <td className="px-3 py-3 text-right rounded-r-xl">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewLatestDiagnosis(req);
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-full text-xs h-8"
                                    title="View latest AI diagnosis"
                                  >
                                    <Eye className="mr-1 h-3 w-3" />
                                    Latest diagnosis
                                  </Button>
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDiagnoseWithAI(req);
                                    }}
                                    variant="secondary"
                                    size="sm"
                                    className="rounded-full text-xs bg-green-50 text-green-700 hover:bg-green-100 border-green-200 h-8"
                                  >
                                    <Zap className="mr-1 h-3 w-3" />
                                    Diagnose with AI
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Past requests */}
        {viewSegment !== "open" && (
          <div className="mt-6">
            <Card className="border shadow-sm bg-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-gray-900">Past requests</CardTitle>
                  {/* PLACEHOLDER: "N resolved this month" — no completion-time filter wired. */}
                  <span className="text-xs text-gray-500">— resolved this month</span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {filteredPastRequests.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600">
                    No past service requests yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-separate border-spacing-y-2">
                      <thead>
                        <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                          <th className="px-3 py-2">Request</th>
                          <th className="px-3 py-2">Property</th>
                          <th className="px-3 py-2">Resolved</th>
                          <th className="px-3 py-2">Vendor</th>
                          <th className="px-3 py-2 text-right">Cost</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPastRequests.map((req) => {
                          const latest = latestDiagnoses[req.id];
                          const topCause = latest?.top_causes?.[0];
                          const probability = typeof topCause?.probability === "number" ? topCause.probability : null;
                          const hasDiagnosisCost =
                            probability !== null && formatEstimatedCost(topCause?.estimated_cost);
                          return (
                          <tr
                            key={req.id}
                            onClick={() => handleViewClick(req)}
                            className="bg-white rounded-xl shadow-sm align-top cursor-pointer hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-3 py-3 rounded-l-xl">
                              <div className="text-sm font-medium text-gray-900">
                                {getRequestTitle(req)}{" "}
                                <span className="text-gray-400 font-normal">&middot; #{req.id}</span>
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-xs">
                                {req.tenant_description || "—"}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-800">
                              {getPropertyName(req.property_id)}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-700">
                              {formatDate(req.service_schedule_date || req.acknowledged_date)}
                            </td>
                            {/* VENDOR — PLACEHOLDER: backend has no vendor field yet */}
                            <td className="px-3 py-3">
                              <span className="inline-flex items-center rounded-full bg-gray-50 text-gray-500 ring-1 ring-gray-200 px-2.5 py-1 text-xs">
                                <Wrench className="mr-1 h-3 w-3" />
                                —
                              </span>
                            </td>
                            {/* COST — derived from the top cause of the latest diagnosis;
                                only rendered when the diagnosis has BOTH a probability and a
                                numeric estimated_cost. Falls back to the muted "$—" placeholder. */}
                            <td className="px-3 py-3 text-sm text-gray-500 text-right">
                              {hasDiagnosisCost ? (
                                <CostBadge
                                  cost={topCause.estimated_cost}
                                  title="Estimated cost from latest AI diagnosis"
                                />
                              ) : (
                                "$—"
                              )}
                            </td>
                            {/* ACTIONS — view latest diagnosis (read-only) */}
                            <td className="px-3 py-3 text-right rounded-r-xl">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewLatestDiagnosis(req);
                                }}
                                variant="outline"
                                size="sm"
                                className="rounded-full text-xs h-8"
                                title="View latest AI diagnosis"
                              >
                                <Eye className="mr-1 h-3 w-3" />
                                Latest diagnosis
                              </Button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        </>
        )}

        {/* Create / Edit Dialog - Outside loading block */}
        <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
          <DialogContent className="sm:max-w-2xl rounded-xl bg-white p-0 gap-0">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-black/10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-600/10 flex items-center justify-center">
                  <Wrench className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <DialogHeader className="p-0 space-y-0">
                    <DialogTitle className="text-gray-900 text-lg font-medium">New Service Request</DialogTitle>
                    <DialogDescription className="text-sm text-gray-500">
                      Capture the tenant&apos;s issue, associate it with a property, and upload photos.
                    </DialogDescription>
                  </DialogHeader>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col">
              <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Tenant Description - highlighted preview box */}
                <div className="rounded-lg bg-gray-100 border border-black/10 p-3">
                  <p className="text-xs text-gray-500 mb-1">
                    Tenant Description <span className="text-red-500">*</span>
                  </p>
                  <textarea
                    name="tenantDescription"
                    required
                    rows={3}
                    value={newRequest.tenantDescription}
                    onChange={handleInputChange}
                    placeholder="Describe the service request issue..."
                    className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none resize-none"
                  />
                </div>

                {/* Tenant & Property - side by side */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">
                      Property <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={newRequest.propertyId}
                      onValueChange={handlePropertyChange}
                    >
                      <SelectTrigger className="rounded-md bg-gray-100 border-0 text-gray-900 text-sm h-10">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id.toString()}>
                            {p.name} – {p.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 mb-1 block">Tenant (optional)</label>
                    <Select
                      value={newRequest.tenantId}
                      onValueChange={(value) => handleInputChange({ target: { name: "tenantId", value } })}
                    >
                      <SelectTrigger className="rounded-md bg-gray-100 border-0 text-gray-900 text-sm h-10">
                        <SelectValue placeholder="Select tenant" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="none">None</SelectItem>
                        {tenants.map((t) => (
                          <SelectItem key={t.id} value={t.id.toString()}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Appliance chips */}
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Appliance</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleInputChange({ target: { name: "applianceId", value: "" } })}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        !newRequest.applianceId
                          ? "bg-green-600 text-white border-green-600"
                          : "bg-white border-black/10 text-gray-700 hover:border-green-600/50"
                      }`}
                    >
                      General Issue
                    </button>
                    {appliances
                      .filter(a => !newRequest.propertyId || a.propertyId === parseInt(newRequest.propertyId))
                      .map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => handleInputChange({ target: { name: "applianceId", value: a.id.toString() } })}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            newRequest.applianceId === a.id.toString()
                              ? "bg-green-600 text-white border-green-600"
                              : "bg-white border-black/10 text-gray-700 hover:border-green-600/50"
                          }`}
                        >
                          {a.name}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Service schedule */}
                <div className="max-w-[50%]">
                  <label className="text-sm text-gray-500 mb-1 block">Service schedule (optional)</label>
                  <Input
                    type="datetime-local"
                    name="serviceScheduleDate"
                    value={newRequest.serviceScheduleDate}
                    onChange={handleInputChange}
                    className="rounded-md bg-white border-black/10 text-sm text-gray-900 h-10"
                  />
                </div>

                {/* Status chips */}
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Status</label>
                  <div className="flex gap-2">
                    {[
                      { value: "pending", label: "Pending", color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock className="h-3.5 w-3.5" /> },
                      { value: "acknowledged", label: "Acknowledged", color: "bg-blue-100 text-blue-700 border-blue-200", icon: <CheckCircle className="h-3.5 w-3.5" /> },
                      { value: "scheduled", label: "Scheduled", color: "bg-green-100 text-green-700 border-green-200", icon: <Clock className="h-3.5 w-3.5" /> },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleInputChange({ target: { name: "requestStatus", value: opt.value } })}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all ${
                          newRequest.requestStatus === opt.value
                            ? `${opt.color} ring-2 ring-offset-1 ring-current/30`
                            : "bg-white border-black/10 text-gray-500 hover:border-gray-400/30"
                        }`}
                      >
                        {opt.icon}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Landlord Description */}
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Landlord Description</label>
                  <textarea
                    name="landlordDescription"
                    rows={3}
                    value={newRequest.landlordDescription}
                    onChange={handleInputChange}
                    placeholder="Add notes or instructions for the maintenance team..."
                    className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 resize-none"
                  />
                </div>

                {/* Photos of issue */}
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Photos of issue</label>
                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-center text-xs text-gray-500 hover:bg-gray-100 transition-colors">
                    <span className="mb-1 text-lg">📷</span>
                    <span>
                      Click to upload or drag &amp; drop
                      <br />
                      <span className="font-medium">(up to 5 images, .jpg / .png)</span>
                    </span>
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                  {fileError && (
                    <p className="mt-2 text-xs text-red-600">{fileError}</p>
                  )}
                  {uploadedFiles.length > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-gray-600">
                      {uploadedFiles.map((file, idx) => (
                        <li key={idx} className="truncate">
                          • {file.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-5 border-t border-black/10">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setShowNewModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={!newRequest.propertyId || !newRequest.tenantDescription}
                >
                  <Wrench className="h-4 w-4 mr-2" />
                  Create Service Request
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Details Modal */}
        <Dialog open={showViewModal} onOpenChange={closeViewModal}>
          <DialogContent className="sm:max-w-2xl rounded-2xl bg-white">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-gray-900">
                    Service Request #{selectedRequest?.id}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-gray-600 mt-1">
                    {isEditing ? "Edit the service request details below." : "View the full details of this service request."}
                  </DialogDescription>
                </div>
                {selectedRequest && (
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusVariants[selectedRequest.request_status]}`}>
                    {selectedRequest.request_status?.replace("_", " ")}
                  </span>
                )}
              </div>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 pt-2">
                {isEditing ? (
                  /* Edit Mode */
                  <form onSubmit={handleUpdateSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-900 text-sm">Property *</Label>
                        <Select
                          value={editFormData.propertyId}
                          onValueChange={(value) => handleEditFormChange({ target: { name: "propertyId", value } })}
                        >
                          <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10">
                            <SelectValue placeholder="Select property" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {properties.map((p) => (
                              <SelectItem key={p.id} value={p.id.toString()}>
                                {p.name} – {p.address}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-900 text-sm">Tenant (optional)</Label>
                        <Input
                          type="number"
                          name="tenantId"
                          value={editFormData.tenantId}
                          onChange={handleEditFormChange}
                          placeholder="Enter tenant ID (optional)"
                          className="rounded-xl bg-white border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 h-10"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-900 text-sm">Appliance (optional)</Label>
                        <Select
                          value={editFormData.applianceId}
                          onValueChange={(value) => handleEditFormChange({ target: { name: "applianceId", value } })}
                        >
                          <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10">
                            <SelectValue placeholder="General issue (no specific appliance)" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {appliances
                              .filter(a => !editFormData.propertyId || a.propertyId === parseInt(editFormData.propertyId))
                              .map((a) => (
                                <SelectItem key={a.id} value={a.id.toString()}>
                                  {a.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-900 text-sm">Service Schedule (optional)</Label>
                        <Input
                          type="datetime-local"
                          name="serviceScheduleDate"
                          value={editFormData.serviceScheduleDate}
                          onChange={handleEditFormChange}
                          className="rounded-xl bg-white border-gray-300 text-sm text-gray-900 h-10"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-900 text-sm">Tenant Description *</Label>
                      <textarea
                        name="tenantDescription"
                        required
                        rows={4}
                        value={editFormData.tenantDescription}
                        onChange={handleEditFormChange}
                        placeholder="Describe the service request issue..."
                        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-900 text-sm">Status</Label>
                      <Select
                        value={editFormData.requestStatus}
                        onValueChange={(value) => handleEditFormChange({ target: { name: "requestStatus", value } })}
                      >
                        <SelectTrigger className="rounded-xl bg-white border-gray-300 text-gray-900 text-sm h-10 w-48">
                          <SelectValue placeholder="Pending" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <DialogFooter className="pt-4">
                      <Button
                        variant="secondary"
                        className="rounded-full text-sm"
                        type="button"
                        onClick={handleEditToggle}
                      >
                        <X className="mr-1 h-3 w-3" />
                        Cancel Edit
                      </Button>
                      <Button
                        type="submit"
                        className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm"
                        disabled={!editFormData.propertyId || !editFormData.tenantDescription}
                      >
                        Save Changes
                      </Button>
                    </DialogFooter>
                  </form>
                ) : (
                  /* View Mode */
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-gray-500 text-xs uppercase tracking-wide">Property</Label>
                        <p className="text-sm text-gray-900 font-medium">{getPropertyName(selectedRequest.property_id)}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-gray-500 text-xs uppercase tracking-wide">Tenant</Label>
                        <p className="text-sm text-gray-900">{selectedRequest.tenant_id ? `Tenant #${selectedRequest.tenant_id}` : "—"}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-gray-500 text-xs uppercase tracking-wide">Appliance</Label>
                        <p className="text-sm text-gray-900">{getApplianceName(selectedRequest.appliance_id) || "General"}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-gray-500 text-xs uppercase tracking-wide">Service Schedule</Label>
                        <p className="text-sm text-gray-900">{formatDate(selectedRequest.service_schedule_date) || "Not scheduled"}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-gray-500 text-xs uppercase tracking-wide">Date Requested</Label>
                        <p className="text-sm text-gray-900">{formatDate(selectedRequest.date_requested)}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-gray-500 text-xs uppercase tracking-wide">Acknowledged Date</Label>
                        <p className="text-sm text-gray-900">{formatDate(selectedRequest.acknowledged_date) || "—"}</p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-gray-500 text-xs uppercase tracking-wide">Tenant Description</Label>
                      <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.tenant_description || "No description provided."}</p>
                      </div>
                    </div>

                    {/* Diagnosis history (lazy-loaded) */}
                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={handleToggleHistoryInModal}
                        className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <History className="h-4 w-4 text-gray-500" />
                          <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">Diagnosis history</span>
                        </span>
                        {showHistoryInModal ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                      {showHistoryInModal && (
                        <div className="bg-gray-50">
                          {diagnosisHistoryLoading[selectedRequest.id] ? (
                            <div className="px-4 py-6 text-center">
                              <LoadingSpinner size="sm" />
                              <p className="text-xs text-gray-500 mt-2">Loading history...</p>
                            </div>
                          ) : diagnosisHistory[selectedRequest.id]?.length > 0 ? (
                            <table className="w-full">
                              <thead>
                                <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
                                  <th className="px-4 py-2 font-medium">Created</th>
                                  <th className="px-4 py-2 font-medium">Disposition</th>
                                  <th className="px-4 py-2 font-medium">Summary</th>
                                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {diagnosisHistory[selectedRequest.id].map((diagnosis, idx) => (
                                  <tr key={diagnosis.id || idx} className="hover:bg-gray-100 transition-colors">
                                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                      {formatDiagnosisDate(diagnosis.date_added)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                                        diagnosis.disposition === "DIY"
                                          ? "bg-emerald-100 text-emerald-700"
                                          : diagnosis.disposition === "DIY_with_caution"
                                          ? "bg-amber-100 text-amber-700"
                                          : "bg-red-100 text-red-700"
                                      }`}>
                                        {diagnosis.disposition?.replace(/_/g, " ") || "—"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700">
                                      {diagnosis.recommendation || diagnosis.top_causes?.[0]?.name || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="rounded-full text-xs h-7"
                                        onClick={() => handleViewHistoryDiagnosis(diagnosis, selectedRequest.id, selectedRequest.appliance_id)}
                                      >
                                        <Eye className="mr-1 h-3 w-3" />
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="px-4 py-6 text-center text-sm text-gray-500">
                              No diagnosis history found. Click "Diagnose with AI" to create one.
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <DialogFooter className="pt-4 flex-wrap gap-2">
                      <Button
                        variant="outline"
                        className="rounded-full text-sm"
                        type="button"
                        onClick={handleEditToggle}
                      >
                        <Pencil className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                      {selectedRequest.request_status === "pending" && (
                        <Button
                          variant="secondary"
                          className="rounded-full text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                          type="button"
                          onClick={async () => {
                            await handleAcknowledge(selectedRequest);
                            closeViewModal();
                          }}
                        >
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        className="rounded-full text-xs bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                        type="button"
                        onClick={() => handleDiagnoseWithAI(selectedRequest)}
                      >
                        <Zap className="mr-1 h-3 w-3" />
                        Diagnose with AI
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm"
                        onClick={closeViewModal}
                      >
                        Okay
                      </Button>
                    </DialogFooter>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* AI Diagnosis Modal */}
        <Dialog open={showDiagnosisModal} onOpenChange={setShowDiagnosisModal}>
          <DialogContent className="sm:max-w-3xl rounded-2xl bg-white max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2">
                <Zap className="h-5 w-5 text-green-600" />
                AI Diagnosis
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-600">
                Request {diagnosisRequestName}
              </DialogDescription>
            </DialogHeader>

            {diagnosisLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <LoadingSpinner size="lg" />
                <p className="text-sm text-gray-600">Analyzing issue with AI...</p>
              </div>
            ) : diagnosisResult ? (
              <>
                <DiagnosisContent result={diagnosisResult} showAiModel={false} />
                <DialogFooter className="pt-4">
                  <Button
                    className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm"
                    onClick={() => setShowDiagnosisModal(false)}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Latest Diagnosis Modal */}
        <Dialog open={showLatestDiagnosisModal} onOpenChange={setShowLatestDiagnosisModal}>
          <DialogContent className="sm:max-w-3xl rounded-2xl bg-white max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2">
                <Eye className="h-5 w-5 text-purple-600" />
                Latest Diagnosis
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-600">
                Request {latestDiagnosisRequestName}
              </DialogDescription>
            </DialogHeader>

            {latestDiagnosisLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <LoadingSpinner size="lg" />
                <p className="text-sm text-gray-600">Fetching latest diagnosis...</p>
              </div>
            ) : latestDiagnosisResult ? (
              <>
                <DiagnosisContent result={latestDiagnosisResult} showAiModel={false} />
                <DialogFooter className="pt-4">
                  <Button
                    className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm"
                    onClick={() => setShowLatestDiagnosisModal(false)}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* History Diagnosis Modal */}
        <Dialog open={showHistoryDiagnosisModal} onOpenChange={setShowHistoryDiagnosisModal}>
          <DialogContent className="sm:max-w-3xl rounded-2xl bg-white max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2">
                <History className="h-5 w-5 text-gray-600" />
                Diagnosis Details
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-600">
                Request {historyDiagnosisRequestName}
                {historyDiagnosisResult?.date_added && (
                  <span className="ml-2">• {formatDiagnosisDate(historyDiagnosisResult.date_added)}</span>
                )}
              </DialogDescription>
            </DialogHeader>

            {historyDiagnosisResult ? (
              <>
                <DiagnosisContent result={historyDiagnosisResult} showAiModel={false} />
                <DialogFooter className="pt-4">
                  <Button
                    className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm"
                    onClick={() => setShowHistoryDiagnosisModal(false)}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Pre-Diagnosis Confirmation Modal */}
        <Dialog open={showPreDiagnosisModal} onOpenChange={closePreDiagnosisModal}>
          <DialogContent className="sm:max-w-2xl rounded-2xl bg-white max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2">
                <Zap className="h-5 w-5 text-green-600" />
                Run AI Diagnosis
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-600">
                Review the information below before running the AI diagnosis. You can add additional context to help the AI provide a better diagnosis.
              </DialogDescription>
            </DialogHeader>

            {preDiagnosisRequest && (
              <div className="space-y-5 pt-2">
                {/* Property Info */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Property</h4>
                  <p className="text-sm font-medium text-gray-900">{getPropertyName(preDiagnosisRequest.property_id)}</p>
                </div>

                {/* Appliance Info */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Appliance</h4>
                  <p className="text-sm font-medium text-gray-900">
                    {(() => {
                      const appliance = appliances.find(a => a.id === preDiagnosisRequest.appliance_id);
                      if (appliance) {
                        return (
                          <>
                            {appliance.name}
                            {(appliance.make || appliance.model) && (
                              <span className="text-gray-500 font-normal">
                                {appliance.make && ` • ${appliance.make}`}
                                {appliance.model && ` ${appliance.model}`}
                              </span>
                            )}
                          </>
                        );
                      }
                      return "General Issue (no specific appliance)";
                    })()}
                  </p>
                </div>

                {/* Tenant Description */}
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <h4 className="text-xs uppercase tracking-wide text-amber-700 mb-2">Tenant Description</h4>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {preDiagnosisRequest.tenant_description || <span className="text-gray-400 italic">No tenant description provided</span>}
                  </p>
                </div>

                {/* Landlord Description Input */}
                <div className="space-y-2">
                  <Label className="text-gray-900 text-sm font-medium">Landlord Description</Label>
                  <p className="text-xs text-gray-500">
                    Add any additional context, observations, or notes that may help the AI provide a more accurate diagnosis.
                  </p>
                  <textarea
                    rows={4}
                    value={preDiagnosisLandlordDescription}
                    onChange={(e) => setPreDiagnosisLandlordDescription(e.target.value)}
                    placeholder="e.g., I inspected the unit and noticed water stains near the appliance. The tenant mentioned it started after the recent storm..."
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                    disabled={preDiagnosisSubmitting}
                  />
                </div>

                {/* Info about what will be sent */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h4 className="text-xs uppercase tracking-wide text-blue-700 mb-2">What the AI will analyze</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    <li>Tenant description of the issue</li>
                    <li>Your landlord description (if provided)</li>
                    <li>Appliance make and model (if available)</li>
                  </ul>
                </div>

                <DialogFooter className="pt-4">
                  <Button
                    variant="secondary"
                    className="rounded-full text-sm"
                    type="button"
                    onClick={closePreDiagnosisModal}
                    disabled={preDiagnosisSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-50 hover:text-green-700 hover:border hover:border-green-600 text-white rounded-full text-sm"
                    onClick={handleConfirmDiagnosis}
                    disabled={preDiagnosisSubmitting}
                  >
                    {preDiagnosisSubmitting ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="mr-1 h-4 w-4" />
                        Run Diagnosis
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
